/**
 * Date utility functions for consistent date formatting across the application
 */

/**
 * Format date to localized date string
 */
export const formatDate = (date: Date | string): string => {
  const d = new Date(date);
  return d.toLocaleDateString('en-US', {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

/**
 * Format time to localized time string
 */
export const formatTime = (date: Date | string): string => {
  const d = new Date(date);
  return d.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

/**
 * Format date and time together
 */
export const formatDateTime = (date: Date | string): string => {
  const d = new Date(date);
  return d.toLocaleString('en-US', {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

/**
 * Format date for input fields (YYYY-MM-DD)
 */
export const formatDateForInput = (date: Date | string): string => {
  const d = new Date(date);
  return d.toISOString().split('T')[0];
};

/**
 * Format datetime for input fields (YYYY-MM-DDTHH:MM)
 */
export const formatDateTimeForInput = (date: Date | string): string => {
  const d = new Date(date);
  return d.toISOString().slice(0, 16);
};

/**
 * Get relative time (e.g., "2 hours ago", "in 3 days")
 */
export const getRelativeTime = (date: Date | string): string => {
  const d = new Date(date);
  const now = new Date();
  const diffInSeconds = Math.floor((d.getTime() - now.getTime()) / 1000);
  const absSeconds = Math.abs(diffInSeconds);

  const intervals = [
    { label: 'year', seconds: 31536000 },
    { label: 'month', seconds: 2592000 },
    { label: 'week', seconds: 604800 },
    { label: 'day', seconds: 86400 },
    { label: 'hour', seconds: 3600 },
    { label: 'minute', seconds: 60 },
  ];

  for (const interval of intervals) {
    const count = Math.floor(absSeconds / interval.seconds);
    if (count >= 1) {
      const suffix = count === 1 ? '' : 's';
      const timeUnit = `${count} ${interval.label}${suffix}`;
      return diffInSeconds < 0 ? `${timeUnit} ago` : `in ${timeUnit}`;
    }
  }

  return diffInSeconds < 0 ? 'just now' : 'in a moment';
};

/**
 * Check if date is today
 */
export const isToday = (date: Date | string): boolean => {
  const d = new Date(date);
  const today = new Date();
  return d.toDateString() === today.toDateString();
};

/**
 * Check if date is tomorrow
 */
export const isTomorrow = (date: Date | string): boolean => {
  const d = new Date(date);
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  return d.toDateString() === tomorrow.toDateString();
};

/**
 * Check if date is yesterday
 */
export const isYesterday = (date: Date | string): boolean => {
  const d = new Date(date);
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  return d.toDateString() === yesterday.toDateString();
};

/**
 * Check if date is in the past
 */
export const isPast = (date: Date | string): boolean => {
  const d = new Date(date);
  const now = new Date();
  return d < now;
};

/**
 * Check if date is in the future
 */
export const isFuture = (date: Date | string): boolean => {
  const d = new Date(date);
  const now = new Date();
  return d > now;
};

/**
 * Get the start of day for a given date
 */
export const getStartOfDay = (date: Date | string): Date => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
};

/**
 * Get the end of day for a given date
 */
export const getEndOfDay = (date: Date | string): Date => {
  const d = new Date(date);
  d.setHours(23, 59, 59, 999);
  return d;
};

/**
 * Get duration between two dates in a human-readable format
 */
export const getDuration = (startDate: Date | string, endDate: Date | string): string => {
  const start = new Date(startDate);
  const end = new Date(endDate);
  const diffInMs = end.getTime() - start.getTime();
  const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
  const diffInHours = Math.floor(diffInMinutes / 60);
  const diffInDays = Math.floor(diffInHours / 24);

  if (diffInDays > 0) {
    const hours = diffInHours % 24;
    if (hours > 0) {
      return `${diffInDays}d ${hours}h`;
    }
    return `${diffInDays}d`;
  }

  if (diffInHours > 0) {
    const minutes = diffInMinutes % 60;
    if (minutes > 0) {
      return `${diffInHours}h ${minutes}m`;
    }
    return `${diffInHours}h`;
  }

  return `${diffInMinutes}m`;
};

/**
 * Format duration in minutes to human-readable format
 */
export const formatDurationFromMinutes = (minutes: number): string => {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;

  if (hours > 0) {
    if (mins > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${hours}h`;
  }

  return `${mins}m`;
};

/**
 * Get timezone-aware date string
 */
export const formatDateWithTimezone = (date: Date | string, timezone?: string): string => {
  const d = new Date(date);

  if (timezone) {
    return d.toLocaleString('en-US', {
      timeZone: timezone,
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZoneName: 'short',
    });
  }

  return formatDateTime(d);
};
